/*
index.tsx
Copyright © 2026 Xerox Corporation. All Rights Reserved.

Copyright protection claimed includes all forms and matters of copyrightable material
and information now allowed by statutory or judicial law or hereinafter granted,
including without limitation, material generated from the software programs which
are displayed on the screen such as icons, screen display looks, etc.
*/

import { useEffect, useCallback, useState, lazy } from 'react'
import { useNavigate } from 'react-router'
import ScrollWrapperLoader from '../../components/scrollWrapperLoader'
const Card = lazy(()=> import('../../components/Card')) 
import { useTranslation } from 'react-i18next'
import {
  deletePreset,
  getPresets
} from '../../modules/httpClients/presetClient'
import { RedactProfile } from '../../modules/redactProfile'
import { useBaseContext } from '../../providers/baseProvider'
import { deviceLanguage } from '../../i18n'
import { useRedactionContext } from '../../providers/redactionProvider'
import { Spinner } from '../../components/Spinner'
import { alphanumericSort } from '../../modules/util'
import DeletePresetModal from '../../components/Modals/deletePresetModal'
import { useToastContext } from '../../providers/useToastContext'
import { ToastType } from '../../types/toastTypes'
import { errorTypes } from '../../constants/errorTypes'
import { isAxiosError } from '../../modules/errors'

export default function SelectProfile () {
  const navigate = useNavigate()
  const baseContext = useBaseContext()
  const redactionContext = useRedactionContext()
  const toastContext = useToastContext()
  const {
    selectedRedactProfile,
    setSelectedRedactProfile,
    setDraftRedactProfile,
    draftRedactProfile,
    redactProfiles,
    setRedactProfiles
  } = redactionContext
  const { setError } = baseContext
  const { t } = useTranslation()
  const [loading, setLoading] = useState(true)
  const [profileIdToDelete, setProfileIdToDelete] = useState<number>()
  const [highlightedProfileId, setHighlightedProfileId] =
    useState<number | null>(null)
  const defaultProfile = redactProfiles.find(p => p.isDefaultProfile)
  const selectedProfileStillExists =
    selectedRedactProfile != null &&
    redactProfiles.some(p => p.id === selectedRedactProfile.id)
  const highlightedProfile = redactProfiles.find(c => c.id == highlightedProfileId)
  const profilePendingDelete = redactProfiles.find(c => c.id == profileIdToDelete)
    
  const loadPresets = useCallback((showLoading = true) => {
    if (showLoading) {
      setLoading(true)
    }

    getPresets(baseContext.deviceId ?? '', deviceLanguage)
      .then((r: RedactProfile[]) => {
        setRedactProfiles(r)
        setLoading(false)
      })
      .catch(e => {
        setLoading(false)
        console.error(e)
        if (isAxiosError(e)) {
          setError(errorTypes.presetsError, false)
        } else {
          setError(errorTypes.apiError)
        }
      })
  }, [baseContext.deviceId, setError, setRedactProfiles])
  useEffect(() => {
    if (redactProfiles.length > 0) {
      setLoading(false)
      return
    }

    loadPresets()
  }, [loadPresets, redactProfiles.length])

  useEffect(() => {
    if (redactProfiles.length === 0) {
      setHighlightedProfileId(null)
      return
    }

    if (defaultProfile) {
      setHighlightedProfileId(defaultProfile.id)
    } else if (selectedProfileStillExists) {
      setHighlightedProfileId(selectedRedactProfile.id)
    } else {
      setHighlightedProfileId(null)
    }
  }, [defaultProfile, redactProfiles, selectedProfileStillExists, selectedRedactProfile])
  const handleOk = () => {
    if (!highlightedProfile) {
      toastContext.addToast(
        t('TOASTS.PRESET_MUST_BE_SELECTED'),
        ToastType.None,
        true,
        true
      )
      return
    }
    //If selecting the same profile, show the draft profile, with draft changes
    if(draftRedactProfile?.id == highlightedProfileId){
    setSelectedRedactProfile(draftRedactProfile)
    }
    else{
    if(highlightedProfile){
    setSelectedRedactProfile(highlightedProfile)
    setDraftRedactProfile(highlightedProfile)
    }
    }
    navigate('/redactionItems')
  }
  const handleCancel = () => {
    navigate('/redactionType')
  }
  const deleteProfile = (profileId: number) => {
    const updatedProfiles = redactProfiles.filter(p => p.id !== profileId)
    setRedactProfiles(updatedProfiles)
    if (highlightedProfileId === profileId) {
      setHighlightedProfileId(updatedProfiles.find(p => p.isDefaultProfile)?.id ?? null)
    }
    deletePreset(profileId.toString())
      .then(() => {
        loadPresets(false)
      })
      .catch(err => {
        console.error(err)
        setError(errorTypes.apiError)
      })
    setProfileIdToDelete(undefined)
  }

  const handleDeleteProfileClicked = (profileId: number) => {
    setProfileIdToDelete(profileId)
  }
  return (
    <>
      {loading ? (
        <div className='row mx-0 justify-content-center align-items-center h-100vh'>
          <Spinner size={170} text={t('LOADING')} />
        </div>
      ) : (
        <Card
          title={t('SELECT_PRESET')}
          onCancel={handleCancel}
          onOk={handleOk}
          XPadding='left'
        >
          <div className='row mx-0'>
            <div className='col-8 px-0'>
              {redactProfiles && redactProfiles.length > 0 && (
                <div className='multi-list-group redaction-presets-table'>
                  <ScrollWrapperLoader>
                    <ul className='xrx-list-group'>
                      {defaultProfile && (
                        <li
                          className={`list-group-item list-group-item-action divider row mx-0 ${
                            highlightedProfileId == defaultProfile.id
                              ? 'active'
                              : ''
                          }`}
                          tabIndex={0}
                          key={defaultProfile.id}
                          onClick={() => setHighlightedProfileId(defaultProfile.id)}
                        >
                          <div className='content-container'>
                            <div className='left-label'>
                              <span>{defaultProfile.name}</span>
                            </div>
                          </div>
                        </li>
                      )}
                      {redactProfiles
                        .filter(c => !c.isDefaultProfile)
                        .sort((a, b) => alphanumericSort(a.name, b.name))
                        .map((r) => {
                          return (
                            <li
                              className={`list-group-item list-group-item-action divider row mx-0 ${
                                highlightedProfileId == r.id ? 'active' : ''
                              }`}
                              tabIndex={0}
                              key={r.id}
                              onClick={() => setHighlightedProfileId(r.id)}
                            >
                              <div className='content-container'>
                                <div className='left-label'>
                                  <span>{r.name}</span>
                                </div>
                                {!r.isDefaultProfile && (
                                  <div className={`right-object borderless-button d-flex justify-content-center align-items-center h-100`}>
                                    <button
                                      type='button'
                                      className='xrx-btn btn-glyph btn-borderless btn-inline remove-recipient-btn h-100'
                                      onClick={e => {
                                        e.stopPropagation()
                                        handleDeleteProfileClicked(r.id)
                                      }}
                                    >
                                      <i className='xgl-trash_two_tone xglsize-32'></i>
                                    </button>
                                  </div>
                                )}
                              </div>
                            </li>
                          )
                        })}
                    </ul>
                  </ScrollWrapperLoader>
                </div>
              )}
            </div>
            <div className='col-4 redaction-profile-details pr-0'>
              {highlightedProfile ? (
                <>
                  {' '}
                  <ScrollWrapperLoader>
                    <div className='col-12 mx-0 pl-0 pr-1 preset-details'>
                        <strong
                          className='preset-details-title pt-2 pb-1'
                          title={highlightedProfile?.name}
                        >
                          {highlightedProfile?.name}
                        </strong>
                      {highlightedProfile?.standardFields.map(standardField => (
                        <div key={standardField.id} className='preset-details-field'>
                          <span
                            className='preset-details-field-label'
                            title={standardField.displayText}
                          >
                            {standardField.displayText}
                          </span>
                          <span className='preset-details-field-separator'>:</span>
                          <span className='preset-details-field-value pl-1 pr-2'>
                            {standardField.isToggled ? t('YES') : t('NO')}
                          </span>
                        </div>
                      ))}
                      {highlightedProfile?.customFields &&
                        highlightedProfile.customFields.length > 0 && (
                          <>
                            <p className='mt-3 mb-0'>
                              <strong>{t('CUSTOM_WORDS_OR_PHRASES')}</strong>
                            </p>
                            {highlightedProfile.customFields.map(
                              (customField, index) => (
                                <div key={index}>
                                  <span>{customField.text}</span>
                                  <br />
                                </div>
                              )
                            )}
                          </>
                        )}
                    </div>
                  </ScrollWrapperLoader>
                </>
              ) : (
                <>
                  <p className='py-1 select-profile-message'>
                    {t('SELECT_PROFILE_FOR_DETAILS')}
                  </p>
                </>
              )}
            </div>
          </div>
        </Card>
      )}
      {profileIdToDelete !== undefined && (
        <DeletePresetModal
          onDelete={() => deleteProfile(profileIdToDelete)}
          isOpen={true}
          onCancel={() => {
            setProfileIdToDelete(undefined)
          }}
          presetName={profilePendingDelete?.name || ""}
        />
      )}
    </>
  )
}
